import { HybridCache } from './hybridCache';
import { backgroundSyncManager } from './backgroundSync';
import { createLogger } from './logger';

const logger = createLogger({ module: 'OfflineFirstCache', level: 'warn' });

interface ConflictResolver {
  resolveConflict<T>(localData: T, serverData: T, metadata: any): T;
}

interface OfflineOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  table: string;
  data: any;
  userId: string;
  timestamp: number;
  retryCount: number;
  priority: number;
}

export class OfflineFirstCache extends HybridCache {
  private conflictResolver: ConflictResolver;
  private offlineOperations: OfflineOperation[] = [];
  private isOnline = navigator.onLine;
  
  constructor() {
    super();
    this.conflictResolver = new DefaultConflictResolver();
    this.setupNetworkListeners();
    this.startOfflineSync();
    logger.info('Offline-first cache initialized');
  }
  
  async getWithOfflineSupport<T>(
    key: string, 
    userId: string,
    fetchFn?: () => Promise<T>,
    options: {
      allowStale?: boolean;
      maxStaleAge?: number;
      backgroundRefresh?: boolean;
    } = {}
  ): Promise<{
    data: T | null;
    status: 'valid' | 'stale_offline' | 'stale_fallback' | 'fresh' | 'not_found';
    source: string;
    isStale?: boolean;
  }> {
    const { allowStale = true, maxStaleAge = 3600000, backgroundRefresh = true } = options;
    
    // Always try cache first
    const cacheResult = await this.get<T>(key, userId);
    
    if (cacheResult.status === 'valid') {
      // Background refresh if data is getting stale and we're online
      if (backgroundRefresh && this.isOnline && this.shouldBackgroundRefresh(cacheResult)) {
        this.backgroundRefresh(key, userId, fetchFn);
      }
      return {
        data: cacheResult.data,
        status: 'valid',
        source: cacheResult.source || 'cache'
      };
    }
    
    // If offline, return cached data even if expired
    if (!this.isOnline && allowStale) {
      const staleData = await this.getStaleData<T>(key, userId);
      if (staleData) {
        const age = Date.now() - (cacheResult.age || 0);
        return {
          data: staleData,
          status: age > maxStaleAge ? 'stale_offline' : 'valid',
          source: 'offline_cache',
          isStale: true
        };
      }
    }
    
    // Online: fetch fresh data
    if (this.isOnline && fetchFn) {
      try {
        const freshData = await fetchFn();
        await this.set(key, freshData, userId);
        return {
          data: freshData,
          status: 'fresh',
          source: 'network'
        };
      } catch (error) {
        logger.warn('Network fetch failed:', error);
        
        // Fallback to stale cache data if allowed
        if (allowStale) {
          const staleData = await this.getStaleData<T>(key, userId);
          if (staleData) {
            return {
              data: staleData,
              status: 'stale_fallback',
              source: 'fallback_cache',
              isStale: true
            };
          }
        }
      }
    }
    
    return {
      data: null,
      status: 'not_found',
      source: 'miss'
    };
  }
  
  async setWithOfflineSupport<T>(
    key: string,
    data: T,
    userId: string,
    options: {
      syncToServer?: boolean;
      table?: string;
      operation?: 'create' | 'update' | 'delete';
      priority?: number;
      optimisticUpdate?: boolean;
    } = {}
  ): Promise<void> {
    const {
      syncToServer = false,
      table,
      operation = 'update',
      priority = 1,
      optimisticUpdate = true
    } = options;
    
    // Always update cache immediately for optimistic updates
    if (optimisticUpdate) {
      await this.set(key, data, userId);
    }
    
    // Queue for server sync if requested
    if (syncToServer && table) {
      if (this.isOnline) {
        // Try immediate sync if online
        try {
          backgroundSyncManager.queueOperation({
            type: operation,
            table,
            data,
            userId,
            priority
          });
        } catch (error) {
          logger.warn('Immediate sync failed, queuing for later:', error);
          this.queueOfflineOperation({
            type: operation,
            table,
            data,
            userId,
            priority
          });
        }
      } else {
        // Queue for later sync when offline
        this.queueOfflineOperation({
          type: operation,
          table,
          data,
          userId,
          priority
        });
      }
    }
  }
  
  async syncOfflineOperations(): Promise<{
    synced: number;
    failed: number;
    conflicts: number;
  }> {
    if (!this.isOnline || this.offlineOperations.length === 0) {
      return { synced: 0, failed: 0, conflicts: 0 };
    }
    
    const results = { synced: 0, failed: 0, conflicts: 0 };
    const operationsToSync = [...this.offlineOperations];
    this.offlineOperations = [];
    
    for (const operation of operationsToSync) {
      try {
        // Check for conflicts before syncing
        const hasConflict = await this.checkForConflicts(operation);
        
        if (hasConflict) {
          const resolved = await this.resolveConflict(operation);
          if (resolved) {
            backgroundSyncManager.queueOperation(operation);
            results.synced++;
          } else {
            results.conflicts++;
            // Re-queue for manual resolution
            this.offlineOperations.push(operation);
          }
        } else {
          backgroundSyncManager.queueOperation(operation);
          results.synced++;
        }
      } catch (error) {
        logger.error('Failed to sync operation:', error);
        results.failed++;
        
        // Retry logic
        if (operation.retryCount < 3) {
          operation.retryCount++;
          this.offlineOperations.push(operation);
        }
      }
    }
    
    return results;
  }
  
  getOfflineOperationsCount(): number {
    return this.offlineOperations.length;
  }
  
  clearOfflineOperations(): void {
    this.offlineOperations = [];
    logger.info('Offline operations queue cleared');
  }
  
  private shouldBackgroundRefresh(cacheResult: any): boolean {
    // Refresh if data is more than 2 minutes old
    return cacheResult.age && cacheResult.age > 120000;
  }
  
  private async backgroundRefresh<T>(
    key: string,
    userId: string,
    fetchFn?: () => Promise<T>
  ): Promise<void> {
    if (!fetchFn) return;
    
    try {
      const freshData = await fetchFn();
      await this.set(key, freshData, userId);
      logger.debug('Background refresh completed', { key });
    } catch (error) {
      logger.warn('Background refresh failed:', error);
    }
  }
  
  private queueOfflineOperation(operation: Omit<OfflineOperation, 'id' | 'timestamp' | 'retryCount'>): void {
    const offlineOp: OfflineOperation = {
      ...operation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retryCount: 0
    };
    
    this.offlineOperations.push(offlineOp);
    logger.debug('Operation queued for offline sync', { 
      type: offlineOp.type, 
      table: offlineOp.table 
    });
  }
  
  private async checkForConflicts(operation: OfflineOperation): Promise<boolean> {
    // Simple conflict detection - check if server data is newer
    try {
      // This would typically involve checking server timestamps
      // For now, assume no conflicts for simplicity
      return false;
    } catch (error) {
      logger.warn('Conflict check failed:', error);
      return false;
    }
  }
  
  private async resolveConflict(operation: OfflineOperation): Promise<boolean> {
    try {
      // Use conflict resolver to merge data
      const resolved = this.conflictResolver.resolveConflict(
        operation.data,
        {}, // Server data would be fetched here
        { operation }
      );
      
      operation.data = resolved;
      return true;
    } catch (error) {
      logger.error('Conflict resolution failed:', error);
      return false;
    }
  }
  
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      logger.info('Network connection restored');
      this.syncOfflineOperations();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      logger.info('Network connection lost');
    });
  }
  
  private startOfflineSync(): void {
    // Sync offline operations every 30 seconds when online
    setInterval(() => {
      if (this.isOnline && this.offlineOperations.length > 0) {
        this.syncOfflineOperations();
      }
    }, 30000);
  }
}

class DefaultConflictResolver implements ConflictResolver {
  resolveConflict<T>(localData: T, serverData: T, metadata: any): T {
    // Default strategy: client wins for user-generated content
    logger.debug('Resolving conflict with client-wins strategy');
    return localData;
  }
}

// Export singleton instance
export const offlineFirstCache = new OfflineFirstCache(); 